import { ANIMATION } from '@alto/design-library-tokens';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Animated, Platform, useWindowDimensions } from 'react-native';
import Modal from 'react-native-modal';
import styled from 'styled-components/native';
import { v4 as uuidv4 } from 'uuid';
import { Constants, useScreenSize } from '../../utils';
import { Column } from '../containers';

const isTest = !!process?.env?.JEST_WORKER_ID;

type NavigationStackComponent = React.ReactElement;

type SetActiveActionSheetOptions = {
  shouldRetainPreviousActionSheet?: boolean;
  dismissible?: boolean;
};

type ActionSheetProviderValue = {
  setActiveActionSheet: (
    ActionSheet: NavigationStackComponent | undefined,
    options?: SetActiveActionSheetOptions,
  ) => void;
  closeActionSheet: () => void;
  overrideActionSheetStack: (newStack: NavigationStackComponent[]) => void;
  goToPreviousActionSheet: () => void;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  restoreStack: <T extends string>(cacheKey: T) => void;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  saveStack: <T extends string>(cacheKey: T) => void;
};

export const ActionSheetContext = React.createContext<ActionSheetProviderValue>({
  setActiveActionSheet: () => null,
  closeActionSheet: () => null,
  overrideActionSheetStack: () => null,
  goToPreviousActionSheet: () => null,
  restoreStack: () => null,
  saveStack: () => null,
});

type InternalActionSheetPropsProviderValue = {
  createAnalyticsEvent: (actionSheetName: string) => void;
  handleBack?: () => void;
  handleClose: () => void;
  isFirstSheet?: boolean;
  dismissible?: boolean;
};

export const INTERNAL_ActionSheetPropsContext = React.createContext<InternalActionSheetPropsProviderValue>({
  createAnalyticsEvent: () => null,
  handleClose: () => null,
});

type ActionSheetProviderProps = {
  readonly children: React.ReactNode;
  readonly createAnalyticsEvent: (actionSheetName: string) => void;
};

type NavigationStackItem = {
  actionSheetComponent: NavigationStackComponent;
  managedProps?: InternalActionSheetPropsProviderValue;
  uuid: string;
  shouldRetainRender: boolean;
};

const StyledModal = styled(Modal)`
  justify-content: flex-end;
  margin: 0;
`;

const StyledVisibilityHack = styled(Column)<{ isHidden: boolean; isMDScreenOrBigger: boolean }>`
  max-height: 90%;
  justify-content: flex-end;
  align-self: flex-end;
  width: 100%;
  ${({ isMDScreenOrBigger }) => {
    if (Platform.OS === 'web' && isMDScreenOrBigger) {
      return 'margin: auto;';
    }
    return '';
  }}
  ${({ isHidden }) =>
    isHidden &&
    `
    opacity: 0;
    z-index: -1;
    height: 0;
  `};
`;

const modalPropsMobile: Partial<React.ComponentProps<typeof Modal>> = {
  animationIn: 'slideInUp',
  animationOut: 'slideOutDown',
  animationInTiming: isTest ? 0 : ANIMATION.SPEEDS.DEFAULT,
  animationOutTiming: isTest ? 0 : ANIMATION.SPEEDS.SLOWEST,
  backdropTransitionInTiming: 0,
  backdropTransitionOutTiming: isTest ? 0 : ANIMATION.SPEEDS.SLOWEST,
  // Ensure backdrop stays visible during close animation
  backdropOpacity: 0.5,
};

const modalPropsDesktopWeb: Partial<React.ComponentProps<typeof Modal>> = {
  animationIn: 'fadeIn',
  animationOut: 'fadeOut',
  animationInTiming: isTest ? 0 : undefined,
  animationOutTiming: isTest ? 0 : ANIMATION.SPEEDS.SLOWEST,
  backdropTransitionInTiming: 0,
  backdropTransitionOutTiming: isTest ? 0 : ANIMATION.SPEEDS.SLOWEST,
  // Ensure backdrop stays visible during close animation
  backdropOpacity: 0.5,
};

export const ActionSheetProvider = ({ children, createAnalyticsEvent }: ActionSheetProviderProps) => {
  const { width, height } = useWindowDimensions();
  const [navigationStack, setNavigationStack] = useState<NavigationStackItem[]>([]);
  // Used for reading the navigationStack without triggering a re-render.
  const virtualizedNavigationStack = useRef<NavigationStackItem[]>(navigationStack);
  virtualizedNavigationStack.current = navigationStack;
  const cache = useRef<Record<string, NavigationStackItem[]>>({});
  const { isMDScreenOrBigger } = useScreenSize();
  const [isContentReady, setIsContentReady] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { actionSheetComponent, managedProps } = navigationStack[navigationStack.length - 1] || {};
  const context = useMemo(
    () => managedProps || { handleClose: () => null, createAnalyticsEvent: () => null },
    [managedProps],
  );

  const handleTransition = useCallback((newStackSetter: Parameters<typeof setNavigationStack>[0]) => {
    // skip animations in tests
    if (isTest) {
      setNavigationStack(newStackSetter);
      return;
    }

    Animated.timing(new Animated.Value(1), {
      toValue: 0,
      duration: ANIMATION.SPEEDS.FAST,
      useNativeDriver: Constants.useNativeDriver,
    }).start(() => {
      setNavigationStack(newStackSetter);
    });
  }, []);

  const handleClose = useCallback(() => {
    // Start closing animation - content will be cleared in onModalHide
    setIsModalVisible(false);
  }, []);

  const handleBack = useCallback(() => {
    // If this is the last item in the stack, close the modal instead of going back
    if (virtualizedNavigationStack.current.length <= 1) {
      setIsModalVisible(false);
      if (isTest) {
        // In tests, clear immediately without waiting for animation
        setIsContentReady(false);
        setNavigationStack([]);
      }
      return;
    }
    handleTransition((prevStack) => prevStack.slice(0, prevStack.length - 1));
  }, [handleTransition]);

  const getManagedProps = useCallback(
    (isFirstSheet: boolean, dismissible?: boolean) => ({
      handleBack: isFirstSheet ? undefined : handleBack,
      handleClose,
      createAnalyticsEvent,
      isFirstSheet,
      dismissible,
    }),
    [handleBack, handleClose, createAnalyticsEvent],
  );

  // Opens the modal and renders content synchronously. We used to gate the first sheet's
  // content on `onModalWillShow`, but under react-native-modal@14.0.0-rc.1 + RN 0.85 +
  // react-native-screens@4.25 + React 19 + enableFreeze, that callback can stall
  // indefinitely on iOS (sheet appears as a black panel until the user taps the screen).
  // Rendering immediately is safe — the content rides the modal's own slideInUp animation.
  const openModal = useCallback(() => {
    setIsContentReady(true);
    setIsModalVisible(true);
  }, []);

  const handleSetActiveActionSheet = useCallback(
    (NextActionSheetComponent: NavigationStackComponent | undefined, options?: SetActiveActionSheetOptions) => {
      if (!NextActionSheetComponent) {
        // Start closing animation - content will be cleared in onModalHide after animation
        setIsModalVisible(false);
        if (isTest) {
          // In tests, clear immediately without waiting for animation
          setIsContentReady(false);
          setNavigationStack([]);
        }
        return;
      }

      const willBeFirstSheet = virtualizedNavigationStack.current.length === 0;
      openModal();

      handleTransition((prevStack) => {
        const nextNavStackItem = {
          actionSheetComponent: NextActionSheetComponent,
          managedProps: getManagedProps(willBeFirstSheet, options?.dismissible),
          uuid: uuidv4(),
          shouldRetainRender: false,
        };
        if (options?.shouldRetainPreviousActionSheet && prevStack.length > 0) {
          // Only retain previous sheet if there actually is one
          const previousNavStackItem = {
            ...prevStack[prevStack.length - 1],
            shouldRetainRender: true,
          };
          const restOfStack = prevStack.slice(0, prevStack.length - 1);
          return [...restOfStack, previousNavStackItem, nextNavStackItem];
        }
        return [...prevStack, nextNavStackItem];
      });
    },
    [handleTransition, openModal, getManagedProps],
  );

  const overrideActionSheetStack = useCallback(
    (newStack: NavigationStackComponent[]) => {
      if (newStack.length === 0) {
        setIsModalVisible(false);
        setIsContentReady(false);
        setNavigationStack([]);
        return;
      }

      const internalStack = newStack.map((stackItem, i) => ({
        actionSheetComponent: stackItem,
        managedProps: getManagedProps(i === 0),
        uuid: uuidv4(),
        shouldRetainRender: false,
      }));

      openModal();
      handleTransition(internalStack);
    },
    [getManagedProps, handleTransition, openModal],
  );

  const goToPreviousActionSheet = useCallback(() => {
    handleBack();
  }, [handleBack]);

  const closeActionSheet = useCallback(() => {
    handleSetActiveActionSheet(undefined);
  }, [handleSetActiveActionSheet]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  const saveStack = <T extends string>(cacheKey: T) => {
    cache.current[cacheKey] = virtualizedNavigationStack.current;
  };
  const saveStackMemoized = useCallback(saveStack, []);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  const restoreStack = <T extends string>(cacheKey: T) => {
    setNavigationStack(cache.current[cacheKey] ?? []);
    cache.current[cacheKey] = [];
  };
  const restoreStackMemoized = useCallback(restoreStack, []);

  const memoizedValue = useMemo(
    () => ({
      setActiveActionSheet: handleSetActiveActionSheet,
      overrideActionSheetStack,
      closeActionSheet,
      goToPreviousActionSheet,
      restoreStack: restoreStackMemoized,
      saveStack: saveStackMemoized,
      isFirstSheet: virtualizedNavigationStack.current.length === 0,
    }),
    [
      handleSetActiveActionSheet,
      closeActionSheet,
      overrideActionSheetStack,
      goToPreviousActionSheet,
      restoreStackMemoized,
      saveStackMemoized,
    ],
  );

  const modalProps = isMDScreenOrBigger ? modalPropsDesktopWeb : modalPropsMobile;

  const handleModalHide = () => {
    // Clear content and stack after the close animation fully completes.
    // Content intentionally remains visible during the animation so the sheet
    // doesn't flicker out before the backdrop finishes fading.
    setIsContentReady(false);
    setIsModalVisible(false);
    setNavigationStack([]);
  };

  const { dismissible, handleClose: managedHandleClose } = managedProps || {};
  const handleBackdropPress = useCallback(() => {
    if (dismissible !== false) {
      managedHandleClose?.();
    }
  }, [dismissible, managedHandleClose]);

  return (
    <ActionSheetContext.Provider value={memoizedValue}>
      {children}
      <INTERNAL_ActionSheetPropsContext.Provider value={context}>
        {(!!actionSheetComponent || isModalVisible) && (
          <StyledModal
            {...(modalProps as React.ComponentProps<typeof Modal>)}
            useNativeDriver={false}
            avoidKeyboard
            isVisible={isModalVisible}
            // On iOS, the native Modal renders in a separate window so e2e (Maestro) cannot
            // see modal content in the view hierarchy. Render in the app hierarchy instead.
            coverScreen={Platform.OS !== 'ios'}
            onModalHide={handleModalHide}
            onBackdropPress={handleBackdropPress}
            onSwipeComplete={managedProps?.handleClose ?? (() => {})}
            propagateSwipe // allow children to receive swipe events (i.e. scroll)
            swipeDirection="down"
            deviceHeight={height}
            deviceWidth={width}
            useNativeDriverForBackdrop={Platform.OS === 'android'} // avoid backdrop flickering on android
            // eslint-disable-next-line react-native/no-inline-styles
            style={{ margin: 0 }} // ensure modal fills entire screen including bottom navigation
          >
            {isContentReady &&
              navigationStack
                .map((actionSheetObject, index) => {
                  if (
                    !actionSheetObject.actionSheetComponent ||
                    (!actionSheetObject.shouldRetainRender && index !== navigationStack.length - 1)
                  ) {
                    return null;
                  }
                  return (
                    <StyledVisibilityHack
                      key={actionSheetObject.uuid || actionSheetObject.actionSheetComponent.key}
                      isHidden={index !== navigationStack.length - 1}
                      isMDScreenOrBigger={isMDScreenOrBigger}
                    >
                      {actionSheetObject.actionSheetComponent}
                    </StyledVisibilityHack>
                  );
                })
                .filter(Boolean)}
          </StyledModal>
        )}
      </INTERNAL_ActionSheetPropsContext.Provider>
    </ActionSheetContext.Provider>
  );
};
